// IPC Global staff on a customer below the top tier only SEE (ENTRAID-307, Hugo Andrade 2026-10-25, Decision 1).
// Display only: the portal refuses every staff customer-admin act itself (portal/backend/lib/staff-act.mjs) with the same
// message. The rule and its wording live once in portal/shared/staff-act.mjs.
import { STAFF_ACT_MESSAGE } from '../../../shared/staff-act.mjs';

export { STAFF_ACT_MESSAGE };

/** True for an IPC Global staff account: an operator or any IPC Global SSO sign-in (GET /me user). */
export const isStaffAccount = (me) => me?.user?.isOperator === true || me?.user?.isStaff === true;

/**
 * True when this staff viewer is read-only on this customer: the record says staff may not act there
 * (`staffMayAct === false`, derived by the portal from the tier and customer zero). An unknown record shows nothing
 * here (the portal still decides); never true for a customer account.
 *
 * @param {object|null} me - the GET /me body
 * @param {{ staffMayAct?: boolean }|null} customer - the customer record as the portal returned it
 */
export function staffReadOnly(me, customer) {
  return isStaffAccount(me) && customer != null && customer.staffMayAct === false;
}

/**
 * The staff activity rows a customer admin sees (GET .../compliance/audit?who=staff): the outcome row of each request,
 * and a 'requested' row only when its outcome is not in the list (still running, or its outcome could not be
 * recorded). Order kept.
 */
export function staffActivityRows(events) {
  const list = Array.isArray(events) ? events : [];
  const settled = new Set(list.filter((e) => e?.result !== 'requested' && e?.requestRef).map((e) => e.requestRef));
  return list.filter((e) => e && (e.result !== 'requested' || !e.requestRef || !settled.has(e.requestRef)));
}

const RESULT_LABELS = Object.freeze({ requested: 'Requested', succeeded: 'Done', refused: 'Refused', failed: 'Failed' });

/** The words for a staff row's result. */
export const staffResultLabel = (result) => RESULT_LABELS[result] ?? 'Recorded';

/**
 * The words for a staff action name (`staff:<METHOD> <path>`, portal/backend/lib/staff-activity.mjs): a known one by
 * name, anything else as the method and path, never raw ids.
 */
const ACTION_LABELS = Object.freeze({
  'staff:POST /deploy': 'Deployed the collector stack',
  'staff:POST /update-code': 'Updated the collector code',
  'staff:POST /fleet/push-code': 'Updated the collector code (all customers)',
  'staff:POST /trigger-run': 'Started a data collection',
  'staff:POST /decommission': 'Decommissioned the customer',
  'staff:POST /restore-access': 'Restored portal access',
  'staff:POST /release-tenant': 'Released the Microsoft Entra tenant',
  'staff:POST /rotate-secret': 'Rotated the Entra credential',
  'staff:POST /onboarding-link': 'Sent an onboarding link',
  'staff:POST /consent': 'Changed the Microsoft Entra connection',
  'staff:PATCH /': 'Changed the customer record',
  'staff:DELETE /': 'Deleted the customer',
  'staff:PATCH /config': 'Changed data collection settings',
  'staff:POST /people': 'Invited a person',
  'staff:PATCH /people/{email}': 'Changed a person\'s roles',
  'staff:DELETE /people/{email}': 'Removed a person',
  'staff:POST /compliance/reports': 'Requested a report',
  'staff:POST /compliance/attestations': 'Recorded an attestation',
  'staff:PUT /compliance/schedule': 'Changed report settings',
  'staff:PUT /company-settings/branding': 'Changed branding',
  'staff:POST /qlik-integration': 'Changed the Qlik Cloud integration',
  'staff:POST /qlik-secret': 'Revealed the Qlik Cloud credentials',
  'staff:PATCH /watch/settings': 'Changed Watch settings',
  'staff:PATCH /watch/kill-switch': 'Changed the Watch kill switch',
});

export function staffActionLabel(action) {
  if (typeof action !== 'string') return 'Action';
  if (Object.hasOwn(ACTION_LABELS, action)) return ACTION_LABELS[action];
  return action.startsWith('staff:') ? action.slice('staff:'.length) : action;
}
